import type { UserOnboarding, UserOnboardingResponse } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { mutate as globalMutate } from "swr";
import useSWRImmutable from "swr/immutable";
import { create } from "zustand";

import { revalidateProfessionCaches } from "~/lib/profession/revalidateProfessionCaches";
import fetcher from "~/utils/fetcher.client";

export type WizardStepKey =
	| "welcome"
	| "profession"
	| "certification"
	| "ceu-goals"
	| "phone"
	| "auth"
	| "complete";

export type LearningPathChoice = "certification" | "ceu";

interface WizardStore {
	step: WizardStepKey;
	setStep: (step: WizardStepKey) => void;
	learningPath: LearningPathChoice | null;
	setLearningPath: (choice: LearningPathChoice | null) => void;
}

const useWizardStore = create<WizardStore>((set) => ({
	step: "welcome",
	setStep: (step) => set({ step }),
	learningPath: null,
	setLearningPath: (learningPath) => set({ learningPath }),
}));

const useOnboarding = () => {
	const { userId } = useAuth();
	const { step, setStep, learningPath, setLearningPath } = useWizardStore();

	const {
		data: onboarding,
		isLoading,
		mutate,
	} = useSWRImmutable<UserOnboardingResponse>(
		userId ? "/users/onboarding" : null,
	);

	const handleUpdate = async (data: UserOnboarding) => {
		await fetcher("/users/onboarding", { method: "PUT", data });
		await mutate();

		if (
			data.primaryProfessionId !== undefined ||
			data.secondaryProfessionId !== undefined
		) {
			await revalidateProfessionCaches(globalMutate);
		}
	};

	return {
		onboarding,
		isLoadingOnboarding: isLoading,
		isOnboardingCompleted: onboarding?.isOnboardingCompleted ?? false,
		mutateOnboarding: mutate,
		handleUpdate,
		step,
		setStep,
		learningPath,
		setLearningPath,
	};
};

export default useOnboarding;
